import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useUserData } from './UserDataContext';
import { GraduationCap, Tag, Upload, X, Image as ImageIcon, Loader2, Copy, Check, Lock, Users, Link, School } from 'lucide-react';
import { toast } from 'sonner';
import { TribeService } from '../services/api';
import { generateInviteCode, buildTribeInviteLink } from '../utils/inviteCode';
import { ImageCropperModal } from './ImageCropperModal';

/**
 * Fix 2: Build the description template from the school name.
 * When schoolName is empty, substitutes "Your school" as a placeholder.
 */
function buildDescription(schoolName: string): string {
    const school = schoolName.trim() || 'Your school';
    return `${school} tribe on AfroYap - a space for students to meet, discuss events, celebrate school culture, and more.`;
}

interface UniversityTribeCreationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function UniversityTribeCreationModal({ open, onOpenChange }: UniversityTribeCreationModalProps) {
    const { userProfile, createTribe } = useUserData();

    const [schoolName, setSchoolName] = useState('');
    const [tribeName, setTribeName] = useState('');
    // Fix 2: description initialised with the template for an empty school name
    const [description, setDescription] = useState(() => buildDescription(''));
    const [loading, setLoading] = useState(false);
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImage, setCropImage] = useState<string | null>(null);
    // School logo state — must be before rank guard (hooks must be unconditional)
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    // Post-creation success state
    const [createdTribeId, setCreatedTribeId] = useState<string | null>(null);
    const [createdInviteCode, setCreatedInviteCode] = useState<string | null>(null);

    // Must be before the rank guard so hooks are called unconditionally
    const [tribeNameTouched, setTribeNameTouched] = useState(false);
    // Fix 2: track whether the user has manually edited the description
    const [userEditedDescription, setUserEditedDescription] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const logoFileInputRef = useRef<HTMLInputElement>(null);

    // Auto-populate tribeName from schoolName
    useEffect(() => {
        if (!tribeNameTouched) {
            setTribeName(schoolName);
        }
    }, [schoolName, tribeNameTouched]);

    // Fix 2: auto-update description from school name, but only if not manually edited
    useEffect(() => {
        if (!userEditedDescription) {
            setDescription(buildDescription(schoolName));
        }
    }, [schoolName, userEditedDescription]);

    // Legion-only guard — all hooks are above this line
    if (userProfile?.rank !== 'legion') return null;

    const inviteLink = createdTribeId && createdInviteCode
        ? buildTribeInviteLink(createdTribeId, createdInviteCode)
        : null;

    const handleReset = () => {
        setSchoolName('');
        setTribeName('');
        setTribeNameTouched(false);
        // Fix 2: reset edited flag so template refills on next open
        setUserEditedDescription(false);
        // description resets automatically via the useEffect when schoolName → '' and userEditedDescription → false
        setBannerFile(null);
        setBannerPreview(null);
        setCropModalOpen(false);
        setCropImage(null);
        if (logoPreview) URL.revokeObjectURL(logoPreview);
        setLogoFile(null);
        setLogoPreview(null);
        setCreatedTribeId(null);
        setCreatedInviteCode(null);
        setCopied(false);
    };

    const handleClose = () => {
        handleReset();
        onOpenChange(false);
    };

    const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
        const reader = new FileReader();
        reader.onloadend = () => { setCropImage(reader.result as string); setCropModalOpen(true); };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const dataURLToBlob = (dataURL: string) => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new Blob([u8arr], { type: mime });
    };

    const handleCropSave = (croppedImageUrl: string) => {
        setBannerPreview(croppedImageUrl);
        const blob = dataURLToBlob(croppedImageUrl);
        setBannerFile(new File([blob], 'university-banner.jpg', { type: 'image/jpeg' }));
        setCropModalOpen(false);
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5MB'); return; }
        // Revoke previous object URL to avoid memory leak
        if (logoPreview) URL.revokeObjectURL(logoPreview);
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
        e.target.value = '';
    };

    const handleSubmit = async () => {
        if (!schoolName.trim()) { toast.error('School name is required'); return; }
        if (!tribeName.trim()) { toast.error('Tribe name is required'); return; }
        if (!description.trim()) { toast.error('Description is required'); return; }

        setLoading(true);
        const inviteCode = generateInviteCode();
        try {
            let bannerUrl = '';
            if (bannerFile && userProfile.id) {
                try {
                    bannerUrl = await TribeService.uploadCommunityBanner(String(userProfile.id), 'tribe', bannerFile);
                } catch {
                    toast.error('Banner upload failed — proceeding without image');
                }
            }

            let schoolLogoUrl = '';
            if (logoFile && userProfile.id) {
                try {
                    schoolLogoUrl = await TribeService.uploadSchoolLogo(String(userProfile.id), logoFile);
                } catch {
                    toast.error('Logo upload failed — proceeding without logo');
                }
            }

            const tribeId = await createTribe({
                name: tribeName.trim(),
                description: description.trim(),
                tags: [],
                bannerUrl,
                bannerImage: bannerUrl,
                tribeType: 'university',
                schoolName: schoolName.trim(),
                ...(schoolLogoUrl && { schoolLogoUrl }),
                requiresApproval: true,
                hiddenOwner: true,
                inviteCode,
                pendingMemberCount: 0,
            });

            setCreatedTribeId(tribeId);
            setCreatedInviteCode(inviteCode);
            window.dispatchEvent(new CustomEvent('REFRESH_COMMUNITIES'));
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || 'Failed to create university tribe');
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        if (!inviteLink) return;
        navigator.clipboard.writeText(inviteLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        // Fix 1: guard onOpenChange with !cropModalOpen so that when ImageCropperModal
        // (a nested Radix Dialog) opens and Radix fires onOpenChange(false) on the parent,
        // we don't call handleClose() and lose all form state.
        // This matches CreateTribeModal's pattern — it passes onOpenChange through without
        // a custom teardown handler, so the parent controls open state.
        <Dialog open={open} onOpenChange={(v) => { if (!v && !cropModalOpen) handleClose(); }}>
            <DialogContent
                className="bg-[#FDFBF7] dark:bg-[#1a1a1a] border-black/5 dark:border-white/10 shadow-2xl"
                // Layout is set inline, not with utility classes: h-[85vh]/max-h-[85vh]/
                // max-w-[95vw]/min-h-0 have no rules in this project's pre-generated
                // stylesheet, so the dialog had no height cap — it grew past the
                // viewport instead of capping and letting the body scroll, which put
                // the "Establish University Tribe" button off-screen on mobile.
                // Same inline approach YapTabGuidelinesModal uses to scroll correctly.
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '95vw',
                    maxWidth: '32rem',
                    height: '85vh',
                    maxHeight: '85vh',
                    padding: 0,
                    gap: 0,
                    overflow: 'hidden',
                }}
            >

                {/* Header */}
                <DialogHeader className="p-6 pb-4 border-b border-black/10 dark:border-white/10 shrink-0" style={{ flexShrink: 0 }}>
                    <DialogTitle className="text-xl font-bold flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-blue-500" />
                        <span>Create University Tribe</span>
                        <span className="ml-auto text-[10px] font-bold px-2 py-1 rounded bg-[#FFD700]/20 text-[#b8860b] dark:text-[#FFD700] uppercase tracking-wider">
                            Legion Only
                        </span>
                    </DialogTitle>
                    <DialogDescription className="text-sm text-black/60 dark:text-white/60">
                        Official university communities. Ownership is hidden until transferred.
                    </DialogDescription>
                </DialogHeader>

                {/* ── SUCCESS VIEW ── */}
                {createdTribeId && inviteLink ? (
                    <div
                        className="custom-scrollbar flex flex-col items-center gap-6 px-6 py-8"
                        style={{
                            flex: '1 1 auto',
                            minHeight: 0, // required for a flex child to shrink and scroll
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            overscrollBehavior: 'contain',
                        }}
                    >
                        <div className="w-16 h-16 rounded-full bg-blue-500/15 flex items-center justify-center">
                            <GraduationCap className="w-8 h-8 text-blue-500" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-black text-black dark:text-white mb-1">University Tribe Created</h3>
                            <p className="text-sm text-black/60 dark:text-white/60">
                                Share this link with the student union admin. They'll be the first owner once they're approved and you transfer ownership.
                            </p>
                        </div>

                        {/* Invite link box */}
                        <div className="w-full space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-black/40 dark:text-white/40 flex items-center gap-1">
                                <Link className="w-3 h-3 text-blue-500" /> Invite Link
                            </Label>
                            <div className="flex items-center gap-2 p-3 rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10">
                                <p className="flex-1 text-xs font-mono text-black/80 dark:text-white/80 break-all leading-relaxed">
                                    {inviteLink}
                                </p>
                                <button
                                    onClick={handleCopy}
                                    className="shrink-0 p-2 rounded-lg bg-black/10 dark:bg-white/10 hover:bg-blue-500/10 transition-colors"
                                >
                                    {copied
                                        ? <Check className="w-4 h-4 text-blue-500" />
                                        : <Copy className="w-4 h-4 text-blue-500" />}
                                </button>
                            </div>
                        </div>

                        {/* Reminder chips */}
                        <div className="w-full space-y-2">
                            <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-200/50 dark:border-blue-800/30 text-xs text-blue-700 dark:text-blue-400">
                                <Lock className="w-3.5 h-3.5 shrink-0 text-blue-500" />
                                Ownership is hidden until you transfer it to the first recipient.
                            </div>
                            <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-200/50 dark:border-blue-800/30 text-xs text-blue-700 dark:text-blue-400">
                                <Users className="w-3.5 h-3.5 shrink-0 text-blue-500" />
                                All join requests require manual approval from the tribe owner.
                            </div>
                        </div>

                        <Button
                            onClick={handleClose}
                            className="w-full bg-linear-to-r from-[#4478ea] to-[#3498DB] text-white font-bold py-6 rounded-xl"
                        >
                            Done
                        </Button>
                    </div>
                ) : (
                    // ── CREATION FORM ──
                    <>
                        <div
                            className="px-6 custom-scrollbar"
                            style={{
                                flex: '1 1 auto',
                                minHeight: 0, // required for a flex child to shrink and scroll
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                overscrollBehavior: 'contain',
                            }}
                        >
                            <div className="py-6 space-y-6">

                                {/* Info banner */}
                                <div className="p-3 rounded-xl bg-[#FFD700]/10 dark:bg-[#FFD700]/5 border border-[#FFD700]/30 space-y-1.5">
                                    <p className="text-[11px] font-bold text-[#b8860b] dark:text-[#FFD700] uppercase tracking-wider">What this creates</p>
                                    <div className="space-y-1 text-xs text-[#b8860b]/80 dark:text-[#FFD700]/70">
                                        <p>· Ownership is hidden until the first transfer</p>
                                        <p>· All joins require manual approval — no auto-join</p>
                                        <p>· A unique invite link is generated on creation</p>
                                    </div>
                                </div>

                                {/* School Name */}
                                <div className="space-y-2">
                                    <Label className="font-bold flex items-center gap-2">
                                        <GraduationCap className="w-4 h-4 text-blue-500" />
                                        School Name <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        value={schoolName}
                                        onChange={(e) => setSchoolName(e.target.value)}
                                        placeholder="e.g. Fordham University"
                                        maxLength={60}
                                        className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 h-12 text-base font-medium rounded-xl"
                                    />
                                </div>

                                {/* Tribe Name */}
                                <div className="space-y-2">
                                    <Label className="font-bold flex items-center gap-2">
                                        <Tag className="w-4 h-4 text-blue-500" />
                                        Tribe Name <span className="text-red-500">*</span>
                                        <span className="text-[10px] font-normal text-black/40 dark:text-white/40 ml-auto">Pre-filled from school name</span>
                                    </Label>
                                    <Input
                                        value={tribeName}
                                        onChange={(e) => { setTribeNameTouched(true); setTribeName(e.target.value); }}
                                        placeholder="e.g. Fordham University"
                                        className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 h-12 text-base font-medium rounded-xl"
                                    />
                                </div>

                                {/* Description — Fix 2: pre-filled template, auto-updates until manually edited */}
                                <div className="space-y-2">
                                    <Label className="font-bold flex items-center justify-between">
                                        <span>Description <span className="text-red-500">*</span></span>
                                        {/* Fix 2: hint disappears once user edits */}
                                        {!userEditedDescription && (
                                            <span className="text-[10px] font-normal text-black/40 dark:text-white/40">
                                                Pre-filled · edit freely
                                            </span>
                                        )}
                                    </Label>
                                    <Textarea
                                        value={description}
                                        onChange={(e) => {
                                            // Fix 2: first keystroke locks the auto-update
                                            setUserEditedDescription(true);
                                            setDescription(e.target.value);
                                        }}
                                        maxLength={200}
                                        className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 min-h-[90px] text-base font-medium rounded-xl p-4"
                                    />
                                </div>

                                {/* Banner Upload */}
                                <div className="space-y-2">
                                    <Label className="flex justify-between items-center font-bold">
                                        <span>Banner Image <span className="font-normal text-black/40 dark:text-white/40">(optional)</span></span>
                                        <span className="text-[10px] text-black/40 dark:text-white/40 uppercase font-bold tracking-wider">Recommended: 1200×300</span>
                                    </Label>
                                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleBannerChange} />
                                    <div onClick={() => fileInputRef.current?.click()} className="relative group cursor-pointer">
                                        <div className={`w-full h-28 rounded-2xl overflow-hidden border-2 border-dashed transition-all flex items-center justify-center bg-black/5 dark:bg-white/5 ${bannerPreview ? 'border-blue-500/50' : 'border-black/10 dark:border-white/10 hover:border-blue-500/30'}`}>
                                            {bannerPreview ? (
                                                <>
                                                    <img src={bannerPreview} className="w-full h-full object-cover" alt="Banner preview" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <Upload className="w-8 h-8 text-blue-500" />
                                                    </div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setBannerFile(null); setBannerPreview(null); }}
                                                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-red-500 transition-colors"
                                                    >
                                                        <X className="w-3.5 h-3.5 text-blue-500" />
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2 text-black/40 dark:text-white/40 group-hover:text-blue-500 transition-colors">
                                                    <ImageIcon className="w-8 h-8 text-blue-500" />
                                                    <p className="text-sm font-bold">Upload Banner</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* School Logo Upload */}
                                <div className="space-y-2">
                                    <Label className="flex justify-between items-center font-bold">
                                        <span className="flex items-center gap-2">
                                            <School className="w-4 h-4 text-blue-500" />
                                            School Logo <span className="font-normal text-black/40 dark:text-white/40">(optional)</span>
                                        </span>
                                        <span className="text-[10px] text-black/40 dark:text-white/40 uppercase font-bold tracking-wider">Min: 400×400</span>
                                    </Label>
                                    <input type="file" ref={logoFileInputRef} className="hidden" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} />
                                    <div onClick={() => logoFileInputRef.current?.click()} className="relative group cursor-pointer flex items-center gap-4">
                                        <div className={`w-20 h-20 rounded-xl overflow-hidden border-2 border-dashed transition-all flex items-center justify-center bg-black/5 dark:bg-white/5 shrink-0 ${logoPreview ? 'border-blue-500/50' : 'border-black/10 dark:border-white/10 hover:border-blue-500/30'}`}>
                                            {logoPreview ? (
                                                <img src={logoPreview} className="w-full h-full object-contain" alt="Logo preview" />
                                            ) : (
                                                <School className="w-7 h-7 text-blue-500/50 group-hover:text-blue-500 transition-colors" />
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-bold text-black/70 dark:text-white/70">
                                                {logoPreview ? 'Change logo' : 'Upload school logo'}
                                            </p>
                                            <p className="text-xs text-black/40 dark:text-white/40 leading-relaxed mt-0.5">
                                                Appears as a circular sticker in the top right of the tribe banner. PNG, JPG, or WEBP.
                                            </p>
                                        </div>
                                        {logoPreview && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); if (logoPreview) URL.revokeObjectURL(logoPreview); setLogoFile(null); setLogoPreview(null); }}
                                                className="w-7 h-7 rounded-full bg-black/20 dark:bg-white/10 flex items-center justify-center hover:bg-red-500 transition-colors shrink-0"
                                            >
                                                <X className="w-3.5 h-3.5 text-white" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>

                        <DialogFooter className="p-6 pt-4 border-t border-black/10 dark:border-white/10 shrink-0 pb-8 sm:pb-6" style={{ flexShrink: 0 }}>
                            <Button variant="ghost" onClick={handleClose}>Cancel</Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={loading || !schoolName.trim() || !tribeName.trim() || !description.trim()}
                                className="w-full bg-linear-to-r from-[#4478ea] to-[#3498DB] hover:opacity-90 text-white py-6 rounded-xl text-lg font-bold shadow-lg disabled:opacity-50"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <Loader2 className="w-5 h-5 animate-spin text-blue-500" /> Creating...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <GraduationCap className="w-5 h-5 text-blue-500" /> Establish University Tribe
                                    </span>
                                )}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>

            {/* Banner cropper — sibling of DialogContent so Radix doesn't dismiss the parent */}
            <ImageCropperModal
                isOpen={cropModalOpen}
                onClose={() => setCropModalOpen(false)}
                imageUrl={cropImage || ''}
                onSave={handleCropSave}
                aspectRatio={4}
                outputWidth={1600}
                title="Adjust University Banner"
                description="Fit the image into the 1200×300 frame"
                circular={false}
                onUploadNew={() => fileInputRef.current?.click()}
            />
        </Dialog>
    );
}
